import * as Sentry from '@sentry/react-router';
import { Links, Meta, Outlet, Scripts, ScrollRestoration, isRouteErrorResponse } from 'react-router';
import type { LinksFunction } from 'react-router';

import { makeApiRequestHeaders } from '~/api/headers';
import { isLoggedIn } from '~/api/is-logged-in';
import { SESSION_COOKIE_ROLLING, sessionCookie } from '~/api/session';
import { apiV2MallKabuandUpdateUserInfo } from '~/api/v2/mall/kabuand/update-user-info';
import type { ApiV2MallKabuandUpdateUserInfoResponse } from '~/api/v2/mall/kabuand/update-user-info';
import { logger } from '~/logger';
import { usePageView } from '~/utils';

import type { Route } from './+types/root';
import { getGlobalConfig } from './api/global-config';
import { SiteChatBot } from './components/site-chatbot';
import { SiteErrorBody } from './components/site-error-body';
import { SiteFooter } from './components/site-footer';
import { SiteHeader } from './components/site-header';
import './tailwind.css';
import { useGetCachedGlobalConfig } from './utils/use-get-cached-global-config';

// 特殊Route系は各モールで仕様を共通にしておき、
// 共有ライブラリかコンポーネントとして管理すると良いと思われる。
// 現段階ではroot.tsx内に記述する。
const specialRoutes = {
  // メンテナンスページのRoute
  UNDER_MAINTENANCE: '/under-maintenance',
};
/**
 * Create response with headers if Set-Cookie header exists
 * Set-Cookieヘッダーが存在する場合、ヘッダー付きのレスポンスを作成
 */
function createResponseWithHeaders<T>(data: T, headers: Headers): Response | T {
  if (headers.get('Set-Cookie')) {
    // Response.json()を使用して適切なヘッダーでJSONレスポンスを作成
    return Response.json(data, { headers });
  }
  return data;
}

export const loader = async ({ request }: Route.LoaderArgs) => {
  const url = new URL(request.url);
  const path = url.pathname;

  // 擬似的に500エラーを発生
  // throw new Response('Internal Server Error', {
  //   status: 500,
  //   statusText: 'ERROR',
  // });

  const globalConfigData = await getGlobalConfig();
  const headers = await makeApiRequestHeaders(request);
  const loggedIn = await isLoggedIn(request);
  let userInfo: ApiV2MallKabuandUpdateUserInfoResponse | undefined = undefined;

  if (loggedIn) {
    try {
      const result = await apiV2MallKabuandUpdateUserInfo(headers);
      userInfo = result;
    } catch (error) {
      logger.error({ error }, 'apiV2MallKabuandUpdateUserInfo error');
      // エラーの場合のデフォルト値
      userInfo = {
        user_rank: 'normal',
        display_balance: -1,
        stock_amount: -1,
      };
    }
  }

  // ローリングクッキーの処理準備
  const responseHeaders = new Headers();

  // セッションクッキーのローリング処理
  if (loggedIn && SESSION_COOKIE_ROLLING) {
    await sessionCookie.refresh(request.headers, responseHeaders);
  }

  const loaderData = { path, globalConfigData, loggedIn, userInfo };

  // 通常のページを表示する
  return createResponseWithHeaders(loaderData, responseHeaders);
};

export const links: LinksFunction = () => [
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  {
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com',
    crossOrigin: 'anonymous',
  },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&family=Noto+Sans+JP:wght@100..900&display=swap',
  },
];

export const Layout = ({ children }: { children: React.ReactNode }) => {
  const GTM_ID = import.meta.env.VITE_APP_GTM_ID;
  const isProduction = import.meta.env.MODE === 'production';

  return (
    <html lang="ja">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href="/favicon.png" type="image/png" />
        <link rel="apple-touch-icon" href="/apple-touch-icon.png" />
        <Meta />
        <Links />
        {GTM_ID && isProduction && (
          <script
            dangerouslySetInnerHTML={{
              __html: `
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','${GTM_ID}');
            `,
            }}
          />
        )}
      </head>
      <body className="text-grey-950 flex min-h-screen flex-col overflow-x-hidden antialiased">
        {GTM_ID && isProduction && (
          <noscript>
            <iframe
              src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
              height="0"
              width="0"
              title="GTM"
              style={{ display: 'none', visibility: 'hidden' }}></iframe>
          </noscript>
        )}
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
};

const App = ({ loaderData: { path, globalConfigData, loggedIn, userInfo } }: Route.ComponentProps) => {
  usePageView();
  const { header: globalConfigHeader, footer: globalConfigFooter } = globalConfigData;
  let Header = SiteHeader;
  let Footer = SiteFooter;
  // 特殊Routeでは、ヘッダー・フッターを差し替える
  if (path === specialRoutes.UNDER_MAINTENANCE) {
    Header = () => <SiteHeader globalConfigHeader={globalConfigHeader} isLogoOnly={true} isLoggedIn={loggedIn} />;
  } else if (
    path === '/' ||
    path === `${import.meta.env.VITE_SERVICE_TOP_ROUTE}` ||
    path === `${import.meta.env.VITE_APPLICATION_TOP_ROUTE}`
  ) {
    Footer = () => <SiteFooter globalConfigFooter={globalConfigFooter} serviceMenus={serviceMenus} />;
  } else if (path.startsWith(`${import.meta.env.VITE_CAMPAIGN_TRANSFER_ROUTE}`)) {
    // ショップ・オファー詳細ページから遷移用ボタンを押した際に表示するページ
    // ヘッダーフッター不要
    Header = () => <></>;
    Footer = () => <></>;
  }
  const { serviceMenus } = globalConfigHeader;
  return (
    <>
      {!path.startsWith(`${import.meta.env.VITE_CAMPAIGN_TRANSFER_ROUTE}`) && <SiteChatBot />}
      <Header globalConfigHeader={globalConfigHeader} isLoggedIn={loggedIn} userInfo={userInfo} />
      <main className="mx-auto w-full">
        <Outlet />
      </main>
      <Footer globalConfigFooter={globalConfigFooter} />
    </>
  );
};
export default App;

export const ErrorBoundary = ({ error }: Route.ErrorBoundaryProps) => {
  const { isLoading, globalConfigHeader, globalConfigFooter } = useGetCachedGlobalConfig();

  if (isLoading) return null;

  // Check for 401 Unauthorized error (session invalid)
  // Since we now throw Response with 401 status, we check using isRouteErrorResponse
  if (isRouteErrorResponse(error) && error.status === 401) {
    // Redirect to session-expired route which handles cookie clearing and redirect
    return (
      <html lang="ja">
        <head>
          <meta charSet="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          {/* Redirect to session-expired route */}
          <meta httpEquiv="refresh" content="0; url=/session-expired" />
        </head>
        <body className="text-grey-950 flex min-h-screen flex-col overflow-x-hidden antialiased">
          <div className="flex min-h-dvh flex-col">
            <main className="mx-auto w-full max-w-screen-md">
              <div className="px-4 py-8 text-center">
                <h1 className="mb-4 text-2xl font-bold">セッションの有効期限が切れました</h1>
                <p className="text-gray-600">
                  セッションの有効期限が切れました。再度ログインが必要です。 自動的にログイン画面へ移動します...
                </p>
              </div>
            </main>
          </div>
        </body>
      </html>
    );
  }

  if (isRouteErrorResponse(error)) {
    const useNormalHeader = new Set([404]);
    if (useNormalHeader.has(error.status)) {
      return (
        <div className="flex min-h-dvh flex-col">
          {globalConfigHeader && <SiteHeader globalConfigHeader={globalConfigHeader} isLoggedIn={false} />}
          <main className="mx-auto w-full max-w-screen-md">
            <SiteErrorBody errorCode={'404'} />
          </main>
          {globalConfigFooter && <SiteFooter globalConfigFooter={globalConfigFooter} />}
        </div>
      );
    }

    return (
      <div className="flex min-h-dvh flex-col">
        {globalConfigHeader && (
          <SiteHeader globalConfigHeader={globalConfigHeader} isLogoOnly={true} isLoggedIn={false} />
        )}
        <main className="mx-auto w-full max-w-screen-md">
          <SiteErrorBody errorCode={'500'} />
        </main>
        {globalConfigFooter && <SiteFooter globalConfigFooter={globalConfigFooter} />}
      </div>
    );
  } else if (error instanceof Error) {
    if (import.meta.env.VITE_SENTRY_DSN) {
      Sentry.captureException(error);
    }
    return (
      <main>
        <h1>Error</h1>
        <p>{error.message}</p>
        <p>The stack trace is:</p>
        <pre>{error.stack}</pre>
      </main>
    );
  } else {
    return <h1>Unknown Error</h1>;
  }
};
